// eslint-disable-next-line max-classes-per-file
import { Model } from 'app/model/backendmodel';

import { Injectable } from '@angular/core';
import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { secretBaseToPasswordOrAKV, passwordOrAKVtoSecretBase } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { validateAKVLinkedserviceReference } from 'app/shared/utils/linkedservice-validation-util';
import { convertDynamicValidatorToConstraint } from '../utls/util';
import { StoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/store-read-settings';
import { mapDatatypeToExpressionType } from 'app/services/datatype-to-expressiontype';
import { StoreReadSettingsAdapter } from 'app/model/adapters/activities/copysource/store-read-settings/store-read-settings.adapter';
import { CopySource } from 'app/model/pipeline/copysource/copysource';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { StoreWriteSettings } from 'app/model/pipeline/copysink/store-write-settings/store-write-settings';
import { StoreWriteSettingsAdapter } from 'app/model/adapters/activities/copysink/store-write-settings/store-write-settings.adapter';
import { CopySink } from 'app/model/pipeline/copysink/copysink';
import { Location } from 'app/model/dataset/location/location';
import { FilePath } from 'app/model/dataset/file-path';
import { Owned } from 'app/model/base/named';
import { BaseAdapter } from 'app/model/adapters/base.adapter';
import { Dataset } from 'app/model/dataset/dataset';
import { parseFileLocation } from 'app/shared/utils/file-path.util';
import { stringIsNullOrEmpty, isString, format } from 'app/shared/utils/string.util';
import { IConnectorProperty } from 'app/services/dynamic-connector.service.models';
import { Constants } from 'app/shared/shared-constants';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { BehaviorSubject } from 'rxjs';
import { artifactDmtsCredentialAuthTypes, ConnectionAuthKind, IAttribute, ILocationPath, ITridentResource, SharedModel } from 'shell-models-lib';

@Injectable({
    providedIn: 'root'
})
export class LinkedServiceDynamicLoaderService {
    public isInitialized$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(private readonly _linkedServiceRegistry: LinkedServiceRegistryService
    ) {
    }

    public initialize(metadatas: ILinkedServiceTypeMetadata[]): void {
        if (this.isInitialized$.value) {
            return;
        }
        this.loadMetadatas(metadatas);
        this.isInitialized$.next(true);
    }

    public loadMetadatas(metadatas: ILinkedServiceTypeMetadata[]): void {
        const self = this;
        metadatas.forEach((metadata) => {
            if (metadata.isFirstClass) {
                this._linkedServiceRegistry.register(metadata);

                if ((__IS_FABRIC_DPE__ || __IS_FABRIC_MDF__) && metadata.dmtsCredentialConnectionType) {
                    this.registerDMTSCredentialForArtifactConnection(metadata.dmtsCredentialConnectionType, metadata);
                }
                return;
            }

            // After generated these models, dynamic generated one should behave this same as first class
            if (metadata.isDynamicallyGenerated) {
                metadata.isFirstClass = true;
            }

            if (this._linkedServiceRegistry.has(metadata.type)) {
                return;
            }

            this._linkedServiceRegistry.register(metadata);

            let modelClass = class extends LinkedService {
                constructor(name?: string, description?: string) {
                    super(name, description);
                }
            };

            // register attribute fisrt
            if (metadata.properties != null) {
                metadata.properties.forEach((p) => {
                    // add akv validators for all credential property
                    const constraint = self.isPropertySensitive(p) ? {
                        validator: validateAKVLinkedserviceReference
                    } : {};
                    Metamodel.attribute({
                        display: p.displayName,
                        association: p.propertyName.toUpperCase(),
                        templatable: p.isParameterizable,
                        accepts: mapDatatypeToExpressionType(p.dataType),
                        constraint
                    })(modelClass.prototype, p.propertyName);
                });
            }

            // register model
            modelClass = Metamodel.model({
                name: metadata.type,
                display: [
                    format(stringResources.ModelName_LinkedServiceTemplate, metadata.displayName),
                    format(stringResources.ModelName_LinkedServicesTemplate, metadata.displayName)
                ]
            })(modelClass) || modelClass;

            let adapterClass = class extends LinkedServiceAdapter<LinkedService> {
                protected supportFirstClassParameterExperience: boolean = true;
                protected modelConstructor(): new () => LinkedService {
                    return modelClass;
                }
                protected model(): IModel {
                    return modelClass.MODEL;
                }

                protected async propertiesToResourceAsync(linkedService: LinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
                    const properties = this.properties(linkedServiceResource);
                    properties.type = Metamodel.modelFor(modelClass).name;
                    properties.typeProperties = {};
                    if (metadata.properties != null) {
                        metadata.properties.forEach((p) => {
                            /* eslint-disable @typescript-eslint/no-explicit-any */
                            properties.typeProperties[p.propertyName] = self.isPropertySensitive(p) ? passwordOrAKVtoSecretBase((linkedService as any)[p.propertyName], this.context) :
                                this.valueToResource(linkedService, Metamodel.modelFor(modelClass).attribute(p.propertyName));
                        });
                        // special handling for connection string
                        const connectionStringProperty = metadata.properties.find((p) => p.controlType === 'ConnectionStringContainer');
                        if (connectionStringProperty != null) {
                            const connectionStringValue = (linkedService as any)[connectionStringProperty.propertyName];
                            if ((metadata.allKnownFieldsInConnectionString || []).every((p) => (linkedService as any)[p] === Constants.maskedCredential)) {
                                //  : this._connectionStringToResource(propertyValue)
                                properties.typeProperties[connectionStringProperty.propertyName] = {
                                    type: 'SecureString',
                                    value: Constants.maskedCredential
                                };
                            } else {
                                properties.typeProperties[connectionStringProperty.propertyName] = this._connectionStringToResource(connectionStringValue);
                            }

                            // for allKnownFieldsInConnectionString, they have been included in connectionString, then they don't show up as property bag in the resource
                            // unless a field is sensitive and is referenced to an akv object
                            metadata.allKnownFieldsInConnectionString?.forEach((p) => {
                                if ((metadata.sensitiveFieldsInConnectionString || []).find((pro) => pro === p) == null || !((linkedService as any)[p] instanceof AzureKeyVault)) {
                                    delete properties.typeProperties[p];
                                }
                            });
                        }
                    }
                    return linkedServiceResource;
                }

                protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: LinkedService): Promise<LinkedService> {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const properties: any = linkedServiceResource.properties;
                    if (metadata.properties != null) {
                        metadata.properties.forEach((p) => {
                            // even if p.propertyName not in properties.typeProperties, we need initialize it
                            const propertyValue = properties.typeProperties[p.propertyName];
                            if (self.isPropertySensitive(p)) {
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                (linkedService as any)[p.propertyName] = self.isPropertySensitive(p) ? secretBaseToPasswordOrAKV(propertyValue, true, this.context) : propertyValue;
                            } else {
                                ValueAdapter.toModel(linkedService, Metamodel.modelFor(modelClass).attribute(p.propertyName), propertyValue);
                            }
                        });
                        // special handling for connection string
                        // the non-sensitive fields in connection string can be splitted in connection string container
                        // for the sensitive fields in connectionstring, if it's not AKV, mark as maskedCredential
                        const connectionStringProperty = metadata.properties.find((p) => p.controlType === 'ConnectionStringContainer');
                        if (connectionStringProperty != null) {
                            const connectionStringValue = properties.typeProperties[connectionStringProperty.propertyName];
                            /* eslint-disable @typescript-eslint/no-explicit-any */
                            if (isString(connectionStringValue)) {
                                // TODO: to parse value for all conection string properties?
                                (linkedService as any)[connectionStringProperty.propertyName] = connectionStringValue;
                            } else if (SecureStringReplacer.isSecureStringObject(connectionStringValue)) {
                                metadata.allKnownFieldsInConnectionString?.forEach((p) => {
                                    const propertyEntity = metadata.properties.find((entity) => entity.propertyName === p);
                                    if (propertyEntity && self.isPropertySensitive(propertyEntity)) {
                                        const propertyValue = properties.typeProperties[p];
                                        (linkedService as any)[p] = secretBaseToPasswordOrAKV(propertyValue, true, this.context);
                                    } else {
                                        (linkedService as any)[p] = Constants.maskedCredential;
                                    }
                                });
                            }
                        }
                    }
                    return linkedService;
                }
            };

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const adaptsTypeProperties: any = {
                encryptedCredential: '*'
            };

            if (metadata.properties != null) {
                metadata.properties.forEach((p) => {
                    adaptsTypeProperties[p.propertyName] = self.isPropertySensitive(p) ? 'secret' : '*';
                });
            }

            adapterClass = AdapterRegistry.adapter({
                model: Metamodel.modelFor(modelClass),
                asynchronous: true,
                adapts: {
                    name: '*',
                    properties: {
                        description: '*',
                        type: '*',
                        connectVia: '*',
                        typeProperties: adaptsTypeProperties
                    }
                }
            })(adapterClass) || adapterClass;

            import('app/model/adapters/adapter-loader').then((m) => {
                m.AdapterLoader.adapters.push(adapterClass);
            });
            // register location model
            this.registerLocationModel(metadata, modelClass);
            // register store read settings and store write settings model and adapter
            this.registerStoreReadSettingsModel(metadata, modelClass);
            this.registerStoreWriteSettingsModel(metadata, modelClass);
        });
    }

    private registerStoreReadSettingsModel(metadata: ILinkedServiceTypeMetadata, lsModelClass: Function): void {
        if (!stringIsNullOrEmpty(metadata.storeReadSettingsType)) {
            let storeReadSettingsModelClass = class extends StoreReadSettings {
                constructor(name?: string, description?: string) {
                    super(name, description);
                }

                public recoverPropFromPrevOne(prevStoreSettings: StoreReadSettings): void {
                    // do nothing
                }
            };
            // register attribute first
            if (metadata.storeReadSettingProperties != null && metadata.storeReadSettingProperties.length > 0) {
                metadata.storeReadSettingProperties.forEach((p) => {
                    const constraint = p.validators != null ? convertDynamicValidatorToConstraint(p.validators) : undefined;
                    Metamodel.attribute({
                        display: p.displayName,
                        association: p.propertyName.toUpperCase(),
                        templatable: p.isParameterizable,
                        accepts: mapDatatypeToExpressionType(p.dataType),
                        constraint
                    })(storeReadSettingsModelClass.prototype, p.propertyName);
                });
            }

            // register model
            storeReadSettingsModelClass = Metamodel.model({
                name: metadata.storeReadSettingsType,
                tags: {
                    linkedServiceModel: Metamodel.modelFor(lsModelClass)
                },
                display: []
            })(storeReadSettingsModelClass) || storeReadSettingsModelClass;

            let storeReadSettingAdapterClass = class extends StoreReadSettingsAdapter<StoreReadSettings, Model.StoreReadSettings> {
                protected override async _toResource(storeReadSettings: StoreReadSettings): Promise<Model.StoreReadSettings> {
                    const resource = await super._toResource(storeReadSettings);
                    if (resource != null && storeReadSettings != null) {
                        metadata.storeReadSettingProperties?.forEach((p) => {
                            /* eslint-disable @typescript-eslint/no-explicit-any */
                            (resource as any)[p.propertyName] =
                                ValueAdapter.toResource(storeReadSettings, Metamodel.modelFor(storeReadSettingsModelClass).attribute(p.propertyName));

                        });
                    }
                    return resource;
                }

                protected override async _toModel(storeReadSettingsResource: Model.StoreReadSettings, copySource: CopySource): Promise<StoreReadSettings> {
                    const model = await super._toModel(storeReadSettingsResource, copySource);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    if (storeReadSettingsResource != null) {
                        metadata.storeReadSettingProperties?.forEach((p) => {
                            ValueAdapter.toModel(model, Metamodel.modelFor(storeReadSettingsModelClass).attribute(p.propertyName), (storeReadSettingsResource as any)[p.propertyName]);
                        });
                    }
                    return model;
                }
            };

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const storeReadSettingAdaptsTypeProperties: any = {
                type: '*'
            };
            metadata.storeReadSettingProperties?.forEach((p) => {
                storeReadSettingAdaptsTypeProperties[p.propertyName] = '*';
            });

            storeReadSettingAdapterClass = AdapterRegistry.adapter({
                model: Metamodel.modelFor(storeReadSettingsModelClass),
                asynchronous: true,
                adapts: {
                    ...storeReadSettingAdaptsTypeProperties
                }
            })(storeReadSettingAdapterClass) || storeReadSettingAdapterClass;

            import('app/model/adapters/adapter-loader').then((m) => {
                m.AdapterLoader.adapters.push(storeReadSettingAdapterClass);
            });
        }
    }

    private registerStoreWriteSettingsModel(metadata: ILinkedServiceTypeMetadata, lsModelClass: Function): void {
        if (!stringIsNullOrEmpty(metadata.storeWriteSettingsType)) {
            let storeWriteSettingsModelClass = class extends StoreWriteSettings {
                constructor(name?: string, description?: string) {
                    super(name, description);
                }
            };

            // register attribute first
            if (metadata.storeWriteSettingProperties != null && metadata.storeWriteSettingProperties.length > 0) {
                metadata.storeWriteSettingProperties.forEach((p) => {
                    const constraint = p.validators != null ? convertDynamicValidatorToConstraint(p.validators) : undefined;
                    Metamodel.attribute({
                        display: p.displayName,
                        association: p.propertyName.toUpperCase(),
                        templatable: p.isParameterizable,
                        accepts: mapDatatypeToExpressionType(p.dataType),
                        constraint
                    })(storeWriteSettingsModelClass.prototype, p.propertyName);
                });
            }

            // register model
            storeWriteSettingsModelClass = Metamodel.model({
                name: metadata.storeWriteSettingsType,
                tags: {
                    linkedServiceModel: Metamodel.modelFor(lsModelClass)
                },
                display: []
            })(storeWriteSettingsModelClass) || storeWriteSettingsModelClass;

            let storeWriteSettingAdapterClass = class extends StoreWriteSettingsAdapter<StoreWriteSettings, Model.StoreWriteSettings> {
                protected override async _toResource(storeWriteSettings: StoreWriteSettings): Promise<Model.StoreWriteSettings> {
                    const resource = await super._toResource(storeWriteSettings);
                    if (resource != null && storeWriteSettings != null) {
                        metadata.storeWriteSettingProperties?.forEach((p) => {
                            /* eslint-disable @typescript-eslint/no-explicit-any */
                            (resource as any)[p.propertyName] =
                                ValueAdapter.toResource(storeWriteSettings, Metamodel.modelFor(storeWriteSettingsModelClass).attribute(p.propertyName));

                        });
                    }
                    return resource;
                }

                protected override async _toModel(storeWriteSettingsResource: Model.StoreWriteSettings, copySink: CopySink): Promise<StoreWriteSettings> {
                    const model = await super._toModel(storeWriteSettingsResource, copySink);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    if (storeWriteSettingsResource != null) {
                        metadata.storeWriteSettingProperties?.forEach((p) => {
                            ValueAdapter.toModel(model, Metamodel.modelFor(storeWriteSettingsModelClass).attribute(p.propertyName), (storeWriteSettingsResource as any)[p.propertyName]);
                        });
                    }
                    return model;
                }
            };

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const storeWriteSettingAdaptsTypeProperties: any = {
                type: '*'
            };
            metadata.storeWriteSettingProperties?.forEach((p) => {
                storeWriteSettingAdaptsTypeProperties[p.propertyName] = '*';
            });

            storeWriteSettingAdapterClass = AdapterRegistry.adapter({
                model: Metamodel.modelFor(storeWriteSettingsModelClass),
                asynchronous: true,
                adapts: {
                    ...storeWriteSettingAdaptsTypeProperties
                }
            })(storeWriteSettingAdapterClass) || storeWriteSettingAdapterClass;

            import('app/model/adapters/adapter-loader').then((m) => {
                m.AdapterLoader.adapters.push(storeWriteSettingAdapterClass);
            });
        }
    }

    private registerLocationModel(metadata: ILinkedServiceTypeMetadata, lsModelClass: Function): void {
        let locationModelClass = class extends Location {
            constructor() {
                super();
            }
            public setLocation(path: FilePath): void {
                return;
            }

            public setParameterizedLocation(path: ILocationPath): void {
                return;
            }

            public getLocation(): FilePath {
                return undefined;
            }

            public getParameterizedLocation(): ILocationPath {
                return undefined;
            }

            public getFolderPath(): string {
                return undefined;
            }

            public isFolder(): boolean {
                return false;
            }

            public isLocationParameterized(): boolean {
                return false;
            }
        };

        // register attribute first
        if (metadata.locationProperties != null && metadata.locationProperties.length > 0) {
            metadata.locationProperties.forEach((p) => {
                const constraint = p.validators != null ? convertDynamicValidatorToConstraint(p.validators) : undefined;
                Metamodel.attribute({
                    display: p.displayName,
                    association: p.propertyName.toUpperCase(),
                    templatable: p.isParameterizable,
                    accepts: mapDatatypeToExpressionType(p.dataType),
                    constraint
                })(locationModelClass.prototype, p.propertyName);
            });
        }

        // update method
        locationModelClass.prototype.setLocation = function setLocation(filePath: FilePath): void {
            if (filePath != null && metadata.locationPattern != null) {
                const locProps = parseFileLocation(filePath.getFullPath(), metadata.locationPattern);
                Object.keys(locProps).forEach((locKey) => {
                    (this as any)[locKey] = locProps[locKey];
                });
            }
        };
        locationModelClass.prototype.getLocation = function getLocation(): FilePath {
            const filePath = new FilePath();
            if (metadata.locationProperties != null) {
                // todo how to combine all properties together?
                filePath.path = metadata.locationProperties.map((property) => (this as any)[property.propertyName])
                    .join('/');
            }
            return filePath;
        };
        locationModelClass.prototype.getFolderPath = function getFolderPath(): string {
            const resultArray: string[] = [];
            if (metadata.locationProperties != null && metadata.locationPattern != null) {
                const fileNameProperty = metadata.locationProperties.find((property) => property.isFileNameProperty);
                const locProps = parseFileLocation(this.getLocation().getFullPath(), metadata.locationPattern);
                Object.keys(locProps).forEach((key) => {
                    // we take join result of all properties except filename property
                    if (fileNameProperty != null && key !== fileNameProperty.propertyName) {
                        resultArray.push((this as any)[key]);
                    }
                });
                return resultArray.join('/');
            }
            return undefined;
        };
        locationModelClass.prototype.isFolder = function isFolder(): boolean {
            const fileNameProperty = (metadata.locationProperties || []).find((property) => property.isFileNameProperty);
            return fileNameProperty != null
                ? stringIsNullOrEmpty((this as any)[fileNameProperty.propertyName]) && Metamodel.modelFor(locationModelClass).attribute(fileNameProperty.propertyName).templateValue(this) == null
                : true;
        };
        locationModelClass.prototype.isLocationParameterized = function isLocationParameterized(): boolean {
            return (metadata.locationProperties || []).some((p) => !stringIsNullOrEmpty(Metamodel.modelFor(locationModelClass).attribute(p.propertyName).templateValue(this)));
        };

        // register model
        locationModelClass = Metamodel.model({
            name: metadata.locationType,
            tags: {
                linkedServiceModel: Metamodel.modelFor(lsModelClass)
            },
            display: []
        })(locationModelClass) || locationModelClass;

        // register adapter
        let locationAdapterClass = class extends BaseAdapter<Location, Model.DatasetLocation> {
            public _toResource(location: Location): Model.DatasetLocation {
                const locationResource: Model.DatasetLocation = {
                    type: Metamodel.modelOf(location).name
                };
                if (locationResource != null && location != null) {
                    metadata.locationProperties?.forEach((p) => {
                        /* eslint-disable @typescript-eslint/no-explicit-any */
                        (locationResource as any)[p.propertyName] =
                            ValueAdapter.toResource(location, Metamodel.modelFor(locationModelClass).attribute(p.propertyName));

                    });
                }
                return locationResource;
            }
            public _toModel(locationResource: Model.DatasetLocation, dataset: Dataset): Location {
                const resourceModel = Metamodel.modelByName((locationResource.type));
                const model = Owned.create<Location>(resourceModel, dataset);
                if (locationResource != null) {
                    metadata.locationProperties?.forEach((p) => {
                        ValueAdapter.toModel(model, Metamodel.modelFor(locationModelClass).attribute(p.propertyName), (locationResource as any)[p.propertyName]);
                    });
                }
                return model;
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const locationAdaptsTypeProperties: any = {
            type: '*'
        };
        metadata.locationProperties?.forEach((p) => {
            locationAdaptsTypeProperties[p.propertyName] = '*';
        });

        locationAdapterClass = AdapterRegistry.adapter({
            model: Metamodel.modelFor(locationModelClass),
            adapts: {
                ...locationAdaptsTypeProperties
            }
        })(locationAdapterClass) || locationAdapterClass;

        import('app/model/adapters/adapter-loader').then((m) => {
            m.AdapterLoader.adapters.push(locationAdapterClass);
        });

    }

    private isPropertySensitive(property: IConnectorProperty): boolean {
        return property.isSensitive;
    }

    private registerDMTSCredentialForArtifactConnection(dmtsCredentialConnectionType: string, artifactLSMetadata: ILinkedServiceTypeMetadata): void {
        // register model
        let dmtsCredentialModelClass = class extends LinkedService {
            public static readonly MODEL: IModel;
            public static readonly ARTIFACT_ID: IAttribute;
            public static readonly AUTHENTICATION_TYPE: IAttribute;

            constructor(name?: string, description?: string) {
                super(name, description);
            }
        };

        Metamodel.attribute({
            display: stringResources.Authentication_Method_Label,
        })(dmtsCredentialModelClass.prototype, 'authenticationType');

        dmtsCredentialModelClass = Metamodel.model({
            name: dmtsCredentialConnectionType,
            display: [
                format(stringResources.ModelName_LinkedServiceTemplate, artifactLSMetadata.displayName),
                format(stringResources.ModelName_LinkedServicesTemplate, artifactLSMetadata.displayName)
            ]
        })(dmtsCredentialModelClass) || dmtsCredentialModelClass;

        const adapterClass = class extends LinkedServiceAdapter<LinkedService> {
            protected supportFirstClassParameterExperience = true;

            protected modelConstructor(): new () => LinkedService {
                return dmtsCredentialModelClass;
            }
            protected model(): IModel {
                return dmtsCredentialModelClass.MODEL;
            }

            protected override toAzureTypeProperties = (
                tridentResource: ITridentResource
            ): Pick<Model.LakehouseLinkedServiceTypeProperties, 'artifactId'|'authenticationType'> => {
                const credentialType = tridentResource.credentialDetails.credentialType;
                let authenticationType: string;
                switch (credentialType) {
                    case ConnectionAuthKind.ServicePrincipal:
                        authenticationType = artifactDmtsCredentialAuthTypes.servicePrincipal;
                        break;
                    case ConnectionAuthKind.OAuth2:
                        authenticationType = artifactDmtsCredentialAuthTypes.userAuth;
                        break;
                    default:
                        authenticationType = undefined;
                }
                return {
                    artifactId: tridentResource.objectId,
                    authenticationType
                };
            };

            protected async propertiesToResourceAsync(
                linkedService: LinkedService,
                linkedServiceResource: Model.LinkedServiceResource
            ): Promise<SharedModel.LinkedServiceResource> {
                const properties = this.properties(linkedServiceResource);
                properties.type = Metamodel.modelFor(dmtsCredentialModelClass).name;

                properties.typeProperties = {
                    artifactId: this.valueToResource(linkedService, Metamodel.modelFor(dmtsCredentialModelClass).attribute('objectId'))
                };

                return linkedServiceResource;
            }
            protected async propertiesToModelAsync(
                linkedServiceResource: Model.LinkedServiceResource,
                linkedService: LinkedService
            ): Promise<LinkedService> {
                const properties = linkedServiceResource.properties as Model.LakehouseLinkedService;
                const typeProperties: Model.LakehouseLinkedServiceTypeProperties = properties.typeProperties;
                ValueAdapter.toModel(linkedService, Metamodel.modelFor(dmtsCredentialModelClass).attribute('objectId'), typeProperties.artifactId);
                ValueAdapter.toModel(linkedService, Metamodel.modelFor(dmtsCredentialModelClass).attribute('authenticationType'), typeProperties.authenticationType);
                return linkedService;
            }
        };

        const dmtsCredentialAdapterClass = AdapterRegistry.adapter({
            model: Metamodel.modelFor(dmtsCredentialModelClass),
            asynchronous: true,
            adapts: {
                name: '*',
                properties: {
                    description: '*',
                    type: '*',
                    typeProperties: {
                        artifactId: '*',
                        authenticationType: '*'
                    }
                }
            }
        })(adapterClass) || adapterClass;

        import('app/model/adapters/adapter-loader').then((m) => {
            m.AdapterLoader.adapters.push(dmtsCredentialAdapterClass);
        });
    }
}
